import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Copy, CheckCircle2, Clock } from "lucide-react";
import { api } from "@/api/client";
import { AuthImage } from "@/components/AuthImage";
import { StatusTimeline, StatusBadge } from "@/components/StatusTimeline";
import { money } from "@/lib/pricing";
import { getTheme } from "@/lib/theme";

export default function OrderDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    const o = await api.getOrder(id);
    setOrder(o);
    if (o.business_id) api.getPaymentInfo(o.business_id).then(setInfo).catch(() => {});
    return o;
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Handle Stripe redirect callbacks
  useEffect(() => {
    const paid = params.get("paid");
    const balance = params.get("balance");
    if (paid === "1" || balance === "1") {
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          await api.getPaymentStatus(id);
          const o = await load();
          if ((balance === "1" && o.balance_paid) || (paid === "1" && o.deposit_paid) || tries > 6) {
            clearInterval(poll);
            if (o.deposit_paid || o.balance_paid) toast.success("Payment received — thank you!");
            setParams({}, { replace: true });
          }
        } catch (e) {
          if (tries > 6) clearInterval(poll);
        }
      }, 1500);
      return () => clearInterval(poll);
    }
    if (paid === "0" || balance === "0") {
      toast("Payment cancelled", { description: "You can try again anytime." });
      setParams({}, { replace: true });
    }
  }, [params, id, load, setParams]);

  const payDeposit = async () => {
    setPaying(true);
    try {
      const origin = window.location.origin;
      const res = await api.createCheckoutSession(id, `${origin}/order/${id}?paid=1`, `${origin}/order/${id}?paid=0`);
      if (res.url) window.location.href = res.url;
    } catch (err) {
      toast.error(err.message || "Could not start payment");
      setPaying(false);
    }
  };

  const payBalance = async () => {
    setPaying(true);
    try {
      const origin = window.location.origin;
      const res = await api.createBalanceCheckoutSession(id, `${origin}/order/${id}?balance=1`, `${origin}/order/${id}?balance=0`);
      if (res.auto_completed) {
        toast.success("Order fully paid!");
        await load();
      } else if (res.url) {
        window.location.href = res.url;
      }
    } catch (err) {
      toast.error(err.message || "Could not start payment");
    } finally {
      setPaying(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied");
  };

  if (loading)
    return (
      <div className="container-app py-16">
        <div className="h-8 w-1/3 rounded animate-pulse" style={{ background: "var(--surface-2)" }} />
      </div>
    );
  if (!order)
    return (
      <div className="container-app py-20 text-center">
        <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Order not found</h1>
        <Link to="/shop" className="btn-brand inline-flex mt-6 px-6 py-3 rounded-full font-semibold">Back to shop</Link>
      </div>
    );

  const theme = getTheme(order.business_id);
  const depositPaid = order.deposit_paid;
  const balancePaid = order.balance_paid;
  const fullyPaid = order.payment_status === "paid" || balancePaid;

  return (
    <div className="container-app py-8 sm:py-12 max-w-4xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--muted)" }}>{theme.name}</div>
          <h1 className="font-display text-4xl" style={{ color: "var(--ink)" }}>Order {order.order_number}</h1>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="mt-8 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
        <div className="space-y-6">
          <div className="rounded-2xl p-6" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
            <h2 className="font-display text-xl mb-4" style={{ color: "var(--ink)" }}>Progress</h2>
            <StatusTimeline status={order.status} />
          </div>

          <div className="rounded-2xl p-6" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
            <h2 className="font-display text-xl mb-4" style={{ color: "var(--ink)" }}>Items</h2>
            <div className="space-y-3">
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between gap-3">
                  <div>
                    <div className="font-semibold" style={{ color: "var(--ink)" }}>
                      {it.quantity}× {it.product_name}
                    </div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>
                      {[it.size, it.flavor].filter(Boolean).join(" · ")}
                      {it.message_on_cake ? ` · "${it.message_on_cake}"` : ""}
                    </div>
                  </div>
                  <div className="font-semibold" style={{ color: "var(--ink)" }}>{order.is_large_order ? <span style={{ color: "var(--muted)" }}>—</span> : it.is_quote ? "Quote" : money(it.subtotal)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 space-y-1.5 text-sm" style={{ borderTop: "1px solid var(--border)" }}>
              {order.is_large_order && order.is_quote ? (
                <div className="flex justify-between font-display text-lg" style={{ color: "var(--ink)" }}><span>Total</span><span style={{ color: "var(--muted)" }}>Pending quote</span></div>
              ) : (
                <>
                  <div className="flex justify-between" style={{ color: "var(--muted)" }}><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
                  <div className="flex justify-between" style={{ color: "var(--muted)" }}><span>Delivery</span><span>{order.delivery_fee ? money(order.delivery_fee) : "Free"}</span></div>
                  <div className="flex justify-between font-display text-lg" style={{ color: "var(--ink)" }}><span>Total</span><span>{money(order.total)}</span></div>
                </>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm" style={{ color: "var(--ink)" }}>
              <div className="rounded-xl p-3" style={{ background: "var(--surface-2)" }}>
                <div className="text-xs" style={{ color: "var(--muted)" }}>Pickup / delivery</div>
                <div className="font-semibold">{order.pickup_date}</div>
                <div className="text-xs capitalize" style={{ color: "var(--muted)" }}>{order.fulfillment_type?.replace("_", " ")}</div>
              </div>
              <div className="rounded-xl p-3" style={{ background: "var(--surface-2)" }}>
                <div className="text-xs" style={{ color: "var(--muted)" }}>Contact</div>
                <div className="font-semibold">{order.customer_name}</div>
                <div className="text-xs" style={{ color: "var(--muted)" }}>{order.customer_phone}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment panel */}
        <div className="rounded-2xl p-6 sticky top-20" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <h2 className="font-display text-xl mb-4" style={{ color: "var(--ink)" }}>Payment</h2>

          {order.is_quote ? (
            <div className="rounded-xl p-4" style={{ background: "var(--accent-soft)", color: "var(--ink)" }} data-testid="awaiting-quote">
              <div className="font-display text-lg">Awaiting your quote</div>
              <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
                {order.is_large_order
                  ? `This is a large / bulk order. ${theme.name} will review your request and send you one total price (they may call ${order.customer_phone}). Once you agree, pay the ${Math.round(order.deposit_percent)}% deposit right here.`
                  : `This is a custom “Etc.” order. ${theme.name} will review your request and contact you at ${order.customer_phone} with a price. Once you agree, you can pay the ${Math.round(order.deposit_percent)}% deposit right here.`}
              </p>
            </div>
          ) : (
          <>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span style={{ color: "var(--muted)" }}>Deposit ({Math.round(order.deposit_percent)}%)</span>
              <span className="flex items-center gap-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                {money(order.deposit_amount)}
                {depositPaid && <CheckCircle2 size={15} className="text-green-600" />}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: "var(--muted)" }}>Balance</span>
              <span className="flex items-center gap-1.5 font-semibold" style={{ color: "var(--ink)" }}>
                {money(order.balance_due)}
                {balancePaid && <CheckCircle2 size={15} className="text-green-600" />}
              </span>
            </div>
          </div>

          <div className="mt-4">
            {fullyPaid ? (
              <div className="rounded-xl p-4 text-center" style={{ background: "#d9f6e4" }}>
                <CheckCircle2 className="mx-auto text-green-700 mb-1" size={24} />
                <div className="font-semibold text-green-800">Fully paid</div>
              </div>
            ) : !depositPaid ? (
              <>
                <button onClick={payDeposit} disabled={paying} data-testid="pay-deposit-btn" className="btn-brand w-full rounded-xl py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <CreditCard size={18} /> {paying ? "Redirecting…" : `Pay deposit ${money(order.deposit_amount)}`}
                </button>
                <div className="mt-3 text-xs text-center" style={{ color: "var(--muted)" }}>Secure Stripe Checkout</div>
              </>
            ) : (
              <>
                <div className="rounded-xl p-3 mb-3 flex items-center gap-2 text-sm" style={{ background: "var(--accent-soft)", color: "var(--ink)" }}>
                  <Clock size={16} /> Deposit paid · balance of {money(order.balance_due)} due to complete.
                </div>
                <button onClick={payBalance} disabled={paying} data-testid="pay-balance-btn" className="btn-brand w-full rounded-xl py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <CreditCard size={18} /> {paying ? "Redirecting…" : `Pay balance ${money(order.balance_due)}`}
                </button>
              </>
            )}
          </div>

          {!fullyPaid && info && (
            <div className="mt-5 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
              <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--muted)" }}>Prefer Zelle / CashApp?</div>
              {info.zelle_handle && (
                <button onClick={() => copy(info.zelle_handle)} className="w-full flex items-center justify-between text-sm py-1.5">
                  <span style={{ color: "var(--muted)" }}>Zelle</span>
                  <span className="flex items-center gap-1.5 font-semibold" style={{ color: "var(--ink)" }}>{info.zelle_handle} <Copy size={13} /></span>
                </button>
              )}
              {info.cashapp_handle && (
                <button onClick={() => copy(info.cashapp_handle)} className="w-full flex items-center justify-between text-sm py-1.5">
                  <span style={{ color: "var(--muted)" }}>CashApp</span>
                  <span className="flex items-center gap-1.5 font-semibold" style={{ color: "var(--ink)" }}>{info.cashapp_handle} <Copy size={13} /></span>
                </button>
              )}
              <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                Send your deposit and mention order {order.order_number}. We&apos;ll confirm shortly.
              </p>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
